<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="dashboard-title">Pulso de la Tienda</h1>
            <p class="text-caption grey--text">
              Resumen operativo &bull; {{ currentDate }}
            </p>
          </div>
        </div>
        <PeriodSelector :loading="loading" default-preset="today" @change="onPeriodChange" class="mt-2" />
      </v-col>
    </v-row>

    <!-- KPI Cards with Deltas -->
    <v-row class="mb-4">
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Ventas" :value="data.ventas" :previous-value="prev.ventas" format="currency" icon="mdi-cash-register" gradient="success-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Ticket Promedio" :value="data.ticket_promedio" :previous-value="prev.ticket_promedio" format="currency" icon="mdi-receipt" gradient="primary-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="UPT" :value="data.upt" :previous-value="prev.upt" format="decimal" icon="mdi-cart-outline" gradient="warning-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Margen Bruto" :value="data.margen" :previous-value="prev.margen" format="percent" icon="mdi-percent" gradient="info-gradient" />
      </v-col>
    </v-row>

    <!-- Resumen extra -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6">
        <v-card class="elevation-0" outlined>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon color="info" class="mr-3" large>mdi-tag-outline</v-icon>
            <div>
              <div class="text-body-2 font-weight-medium">Descuentos otorgados</div>
              <div class="text-h6 font-weight-bold">{{ formatCurrency(data.descuentos) }}</div>
              <div class="text-caption grey--text">Tasa: {{ data.tasa_descuento }}%</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6">
        <v-card class="elevation-0" outlined>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon color="purple" class="mr-3" large>mdi-account-group</v-icon>
            <div>
              <div class="text-body-2 font-weight-medium">Transacciones</div>
              <div class="text-h6 font-weight-bold">{{ data.transacciones }}</div>
              <div class="text-caption grey--text">{{ data.unidades }} unidades vendidas</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tendencia 7 días + Día de semana -->
    <v-row class="mb-4">
      <v-col cols="12" md="7">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
            Tendencia &uacute;ltimos 7 d&iacute;as
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="trend7dChart.length > 1"
              type="LineChart"
              :data="trend7dChart"
              :options="trendOptions"
              class="chart-container-sm"
            />
            <div v-else class="text-center grey--text pa-4">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="orange">mdi-calendar-week</v-icon>
            Ventas por D&iacute;a de Semana
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="dowChart.length > 1"
              type="BarChart"
              :data="dowChart"
              :options="dowChartOptions"
              class="chart-container-sm"
            />
            <div v-else class="text-center grey--text pa-4">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tables -->
    <v-row>
      <!-- Top Products -->
      <v-col cols="12" md="7">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="success">mdi-trending-up</v-icon>
            Top 10 Productos
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="productHeaders"
            :items="data.top_products"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin ventas para este periodo"
          >
            <template v-slot:item.venta="{ item }">{{ formatCurrency(item.venta) }}</template>
            <template v-slot:item.utilidad="{ item }">{{ formatCurrency(item.utilidad) }}</template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.top_products.length" class="text-center grey--text pa-4">Sin ventas</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.top_products" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }} - {{ item.refe }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.color }} &bull; {{ item.qty }} und</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="text-right ma-0">
                  <div class="text-body-2 font-weight-bold">{{ formatCurrency(item.venta) }}</div>
                  <div class="text-caption grey--text">Util: {{ formatCurrency(item.utilidad) }}</div>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <!-- Formas de Pago -->
      <v-col cols="12" md="5">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="primary">mdi-credit-card-outline</v-icon>
            Formas de Pago
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="paymentHeaders"
            :items="data.payments"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="20"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.total="{ item }">{{ formatCurrency(item.total) }}</template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.payments.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.payments" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.tipo }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.qty }} operaciones</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <div class="text-body-2 font-weight-bold">{{ formatCurrency(item.total) }}</div>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver, formatCurrency } from "@/services/webserver.js";
import { GChart } from "vue-google-charts";
import PeriodSelector from "@/components/PeriodSelector.vue";
import KpiDeltaCard from "@/components/KpiDeltaCard.vue";

const DOW_NAMES = ["", "Dom", "Lun", "Mar", "Mi\u00e9", "Jue", "Vie", "S\u00e1b"];

export default {
  name: "StoreDashboard",
  components: { GChart, PeriodSelector, KpiDeltaCard },
  data() {
    return {
      loading: false,
      period: {},
      data: {
        ventas: 0, costo: 0, transacciones: 0, unidades: 0, descuentos: 0,
        ticket_promedio: 0, upt: 0, margen: 0, utilidad: 0, tasa_descuento: 0,
        top_products: [], payments: [], sales_by_dow: [], trend_7d: [],
        prev: null,
      },
      productHeaders: [
        { text: "Marca", value: "marca", sortable: false },
        { text: "Ref", value: "refe", sortable: false },
        { text: "Color", value: "color", sortable: false },
        { text: "Cant.", value: "qty", align: "end" },
        { text: "Venta", value: "venta", align: "end" },
        { text: "Utilidad", value: "utilidad", align: "end" },
      ],
      paymentHeaders: [
        { text: "Forma de Pago", value: "tipo", sortable: false },
        { text: "Qty", value: "qty", align: "center" },
        { text: "Total", value: "total", align: "end" },
      ],
      trendOptions: {
        legend: { position: "none" },
        curveType: "function",
        colors: ["#1a73e8"],
        chartArea: { width: "85%", height: "75%" },
        hAxis: { textStyle: { fontSize: 10 } },
        vAxis: { format: "short" },
        height: 220,
      },
      dowChartOptions: {
        legend: { position: "none" },
        colors: ["#f9ab00"],
        chartArea: { width: "65%", height: "80%" },
        hAxis: { format: "short" },
        vAxis: { textStyle: { fontSize: 11 } },
        height: 220,
      },
    };
  },
  computed: {
    isMobile() { return this.$vuetify.breakpoint.xs; },
    currentDate() {
      return new Date().toLocaleDateString("es-CO", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    },
    prev() {
      return this.data.prev || {
        ventas: null, ticket_promedio: null, upt: null, margen: null
      };
    },
    trend7dChart() {
      const d = this.data.trend_7d || [];
      if (!d.length) return [["Fecha", "Ventas"]];
      return [["Fecha", "Ventas"], ...d.map((r) => [r.order_date, parseFloat(r.ventas)])];
    },
    dowChart() {
      const d = this.data.sales_by_dow || [];
      if (!d.length) return [["D\u00eda", "Ventas Prom."]];
      return [
        ["D\u00eda", "Ventas Prom."],
        ...d.map((r) => [DOW_NAMES[parseInt(r.dow)] || r.dow, parseFloat(r.ventas_promedio)])
      ];
    },
  },
  methods: {
    formatCurrency,
    onPeriodChange(period) {
      this.period = period;
      this.loadData();
    },
    loadData() {
      if (!this.period.dateFrom) return;
      this.loading = true;
      const store = this.$store.state.profile.store_id || window.store?.store_id;
      webserver("get_store_dashboard", {
        store,
        date_from: this.period.dateFrom,
        date_to: this.period.dateTo,
        compare_from: this.period.compareDateFrom,
        compare_to: this.period.compareDateTo,
      }, (res) => {
        this.loading = false;
        if (res && !res.error) {
          this.data = { ...this.data, ...res };
        }
      });
    },
  },
};
</script>

<style scoped>
.dashboard-title { font-size: 1.5rem; font-weight: 700; color: #424242; }
.section-title { font-size: 0.9375rem; font-weight: 600; color: #424242; }
.analyst-table { font-size: 0.8125rem; }
.chart-container-sm { width: 100%; min-height: 200px; }
.mobile-item { min-height: 48px; border-bottom: 1px solid #f0f0f0; }
.mobile-item:last-child { border-bottom: none; }
</style>
